import { exhaustiveGuard, isNil, isStringLiteralOfType, type Nil } from "@mcwd/typescript-type-guards";
import type { FormObjectWithDocument, FormObjectWithVideo, FormWidgetData, VideoResource, VideoResourceWithDocs } from "../../../widgetDataTs.js";
import { GatingLevel, type GatingLevelFor } from "../../types/definitions/gating/gating-level.js";
import type { IFormSettings } from "../../form-settings/form-settings-factory.js";
import { getGatingLevelFromResources } from "./get-gating-level-from-resources-only.js";
import type { AnyFormSettingsKey } from "../../form-settings/form-experience/form-settings-keys.js";
import { FormOrFormExperienceType } from "./FormOrFormExperienceType.js";


export function getGatingLevelFromWidgetData<TFormOrExperience extends FormOrFormExperienceType>(
  type: TFormOrExperience,
  formSettingsKey: AnyFormSettingsKey,
  settings: IFormSettings,
  widgetData: FormWidgetData
): GatingLevelFor<TFormOrExperience> | Nil 
{
  type ThisReturnType = GatingLevelFor<TFormOrExperience> | Nil;

  const { gateLevelSource, allowedGatingStates } = settings;

  const allowedFormGatingLevels = allowedGatingStates.filter(a => GatingLevel.MarketoForm.includes(a as GatingLevel.MarketoForm)) as GatingLevel.MarketoForm[]; 

  const hasDocGatingSrc = gateLevelSource.includes("Document");
  const hasVidGatingSrc = gateLevelSource.includes("Video");
  const hasSetupGatingSrc = gateLevelSource.includes("WidgetDataSetup");

  if (!hasDocGatingSrc && !hasVidGatingSrc && !hasSetupGatingSrc){
    return null;
  }

  let gatingResources: { gatingLevel: GatingLevel.Resource.Raw }[] = [];
  const form = widgetData.setup.form;
  if (hasDocGatingSrc && ('document' in form) && ('selected' in form.document)) {
      gatingResources = gatingResources.concat((form as FormObjectWithDocument<"Final">).document.selected);
  }
  if (hasVidGatingSrc && ('video' in form) && ('selected' in form.video)) {
      gatingResources = gatingResources.concat((form as FormObjectWithVideo<"Final", VideoResource | VideoResourceWithDocs>).video.selected);
  }
  if (hasSetupGatingSrc && ("gatingLevel" in widgetData.setup) && isStringLiteralOfType(widgetData.setup.gatingLevel, GatingLevel.Resource.Raw)) {
    gatingResources.push({ gatingLevel: widgetData.setup.gatingLevel });
  }

  let resourceGating: ThisReturnType = null;
  if (gatingResources.length > 0) {
    resourceGating = getGatingLevelFromResources(type, settings, gatingResources);
  }
  if (isNil(resourceGating)) {
    return null;
  }
  if (isStringLiteralOfType(type, FormOrFormExperienceType))
  {
    const allowedValues = (type === "MarketoForm") ? allowedFormGatingLevels : allowedGatingStates;
    if (allowedValues.includes(resourceGating as typeof allowedValues[ number ]))
    {
      return resourceGating;
    }
    throw new Error(`Resource Gating Level '${ resourceGating }' is an invalid [GatingLevel.${ type }] value for form experience '${ formSettingsKey }'`);
  }
  exhaustiveGuard(type);
}